import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import packRepository from '../../services/repositories/pack.repository';
import '../../styles/products.scss';

const PackIndex = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPacks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await packRepository.getAll();
      
      // Handle different response formats
      let transformedPacks = [];
      if (response) {
        if (Array.isArray(response)) {
          transformedPacks = response;
        } else if (response.data && Array.isArray(response.data)) {
          transformedPacks = response.data;
        } else if (typeof response === 'object') {
          transformedPacks = [response];
        }
      }
      
      const formattedPacks = transformedPacks.map(pack => ({
        id: pack.id,
        name: pack.name || '-',
        price: pack.price || 0,
        quantity: pack.quantity || 0,
        status: pack.status || 'active',
        products: pack.products || [],
        created_at: pack.created_at,
      }));
      
      setPacks(formattedPacks);
    } catch (err) {
      console.error('Error fetching packs:', err);
      setError(err.message || 'Erreur lors du chargement des packs');
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      header: 'ID',
      accessor: 'id',
      sortable: true,
      render: (value) => `#${value}`,
    },
    {
      header: 'Nom',
      accessor: 'name',
      sortable: true,
    },
    {
      header: 'Prix',
      accessor: 'price',
      sortable: true,
      render: (value) => `${value} DH`,
    },
    {
      header: 'Quantité',
      accessor: 'quantity',
      sortable: true,
    },
    {
      header: 'Statut',
      accessor: 'status',
      type: 'status',
      sortable: true,
    },
    {
      header: 'Produits',
      accessor: 'products',
      render: (value) => {
        if (Array.isArray(value) && value.length > 0) {
          return (
            <span className="badge bg-info">
              {value.length} produit(s)
            </span>
          );
        }
        return <span className="text-muted">Aucun produit</span>;
      },
    },
    {
      header: 'Date création',
      accessor: 'created_at',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString('fr-FR'),
    },
  ], []);

  const handleAdd = useCallback(() => {
    navigate('/admin/packs/pack-add');
  }, [navigate]);

  const handleEdit = useCallback((pack) => {
    navigate(`/admin/packs/pack-edit/${pack.id}`);
  }, [navigate]);

  const handleView = useCallback((pack) => {
    navigate(`/admin/packs/pack-show/${pack.id}`);
  }, [navigate]);

  const handleDelete = useCallback(async (pack) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le pack "${pack.name}" ?`)) {
      try {
        setLoading(true);
        await packRepository.delete(pack.id);
        await fetchPacks();
      } catch (err) {
        console.error('Error deleting pack:', err);
        setError('Erreur lors de la suppression du pack');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchPacks]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} pack(s) ?`)) {
      try {
        setLoading(true);
        for (const id of selectedIds) {
          await packRepository.delete(id);
        }
        await fetchPacks();
      } catch (error) {
        console.error('Bulk delete error:', error);
        setError('Erreur lors de la suppression des packs');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchPacks]);

  useEffect(() => {
    fetchPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <div className="packs-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <DataTable
        data={packs}
        columns={columns}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onView={handleView}
        onDelete={handleDelete}
        onBulkAction={handleBulkDelete}
        title="Packs List"
        addButtonText="Nouveau Pack"
        searchPlaceholder="Rechercher des packs..."
        pageSize={10}
        selectable={true}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default PackIndex;